/**
 * "Kickoff Data" AI-enrichment panel (TIER-2 fidelity parity).
 *
 * 1:1 port of the legacy SPA `../EER app/public/assets/js/project.js`:
 *   - `renderKickoffPanel` (~1540-1589): a collapsible `<details>` (magic-wand
 *     title + `enriched_at` date) with up to seven clickable one-line sections —
 *     Summary / Company / Position / Requirements / Compensation / Search-Sourcing
 *     / Missing data — each colour-coded and truncated;
 *   - `openKickoffDetail` (~1613-1662): clicking a section opens a detail dialog
 *     with the legacy sub-fields (Company: name/industry/HQ/employees/founded/
 *     website/description; Position: title/location/remote/type/…; Requirements:
 *     experience/education/must-have/nice-to-have/languages; Compensation:
 *     salary/bonus/benefits/equity; Sourcing: keywords/target-industries/
 *     target-companies/DNS-companies; Missing: the gaps list).
 *
 * Thin shell — the mapping lives in `./kickoffEnrichmentModel.ts`
 * (buildKickoffPreviews / buildKickoffDetail / enrichedAtLabel) and the detail
 * chrome is the shared `ui/Dialog.tsx`. The panel renders nothing when the project
 * carries no `kickoff_enrichment` (legacy `if (!ke) return ''`).
 */
import { useState } from 'react';
import {
  buildKickoffPreviews,
  buildKickoffDetail,
  enrichedAtLabel,
  type KickoffEnrichmentSectionKey,
} from './kickoffEnrichmentModel.js';
import { Dialog } from '../ui/Dialog.js';

/** Colour accent per section (legacy border-l-4 / bg pairs in renderKickoffPanel). */
const SECTION_ACCENT: Record<KickoffEnrichmentSectionKey, string> = {
  summary: 'border-emerald-400 bg-emerald-50',
  company_profile: 'border-slate-300 bg-white',
  position: 'border-blue-300 bg-white',
  requirements: 'border-amber-300 bg-white',
  compensation: 'border-green-300 bg-white',
  sourcing: 'border-purple-300 bg-white',
  missing_info: 'border-red-300 bg-red-50',
};

export interface KickoffDataPanelProps {
  /** The `projects.data.kickoff_enrichment` bag (null when never enriched). */
  readonly enrichment?: Record<string, unknown> | null;
  /** Whether the `<details>` starts expanded. Default true (legacy `open!==false`). */
  readonly defaultOpen?: boolean;
}

export function KickoffDataPanel({
  enrichment,
  defaultOpen = true,
}: KickoffDataPanelProps): React.JSX.Element | null {
  const [openKey, setOpenKey] = useState<KickoffEnrichmentSectionKey | null>(null);

  const previews = buildKickoffPreviews(enrichment);
  // Legacy: render nothing when there are no non-empty sections.
  if (previews.length === 0) return null;

  const dateLabel = enrichedAtLabel(enrichment);
  const detail = openKey ? buildKickoffDetail(enrichment, openKey) : null;

  return (
    <details
      data-testid="kickoff-data-panel"
      className="rounded-lg border border-border p-4"
      open={defaultOpen}
    >
      <summary className="cursor-pointer select-none text-sm font-bold text-text-strong">
        <i className="fa-solid fa-wand-magic-sparkles mr-1 text-emerald-500" aria-hidden />
        Kickoff Data
        {dateLabel ? (
          <span
            data-testid="kickoff-data-date"
            className="ml-1 text-xs font-normal text-text-muted"
          >
            {dateLabel}
          </span>
        ) : null}
      </summary>

      <div className="mt-2 space-y-2">
        {previews.map((row) => (
          <button
            key={row.key}
            type="button"
            data-testid={`kickoff-data-section-${row.key}`}
            onClick={() => setOpenKey(row.key)}
            className={`w-full rounded border-l-4 p-3 text-left text-xs text-slate-700 transition-opacity hover:opacity-80 ${SECTION_ACCENT[row.key]}`}
          >
            <strong>
              {row.label}
              {row.key === 'missing_info' ? '' : ':'}
            </strong>
            {row.preview ? ` ${row.preview}` : ''}
          </button>
        ))}
      </div>

      {/* Per-section detail dialog (openKickoffDetail). */}
      <Dialog
        open={openKey !== null}
        onClose={() => setOpenKey(null)}
        title={detail?.title ?? ''}
        testId="kickoff-data-detail-dialog"
        maxWidth="2xl"
      >
        {detail === null ? (
          <p data-testid="kickoff-data-detail-empty" className="text-sm text-text-muted">
            No data
          </p>
        ) : (
          <div className="space-y-3">
            {detail.paragraph ? (
              <p
                data-testid="kickoff-data-detail-paragraph"
                className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700"
              >
                {detail.paragraph}
              </p>
            ) : null}

            {detail.rows.length > 0 ? (
              <table className="w-full text-sm">
                <tbody>
                  {detail.rows.map((r, i) => (
                    <tr key={r.label || `row-${i}`} data-testid="kickoff-data-detail-row">
                      {r.label ? (
                        <td className="whitespace-nowrap py-1.5 pr-3 align-top text-text-muted">
                          {r.label}
                        </td>
                      ) : null}
                      <td className={`py-1.5 ${r.label ? '' : 'w-full'}`} colSpan={r.label ? 1 : 2}>
                        {r.items ? (
                          <ul className="ml-4 list-disc space-y-0.5 text-slate-700">
                            {r.items.map((item, j) => (
                              <li key={j}>{item}</li>
                            ))}
                          </ul>
                        ) : r.href ? (
                          <a
                            href={r.href}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 underline"
                          >
                            {r.value}
                          </a>
                        ) : (
                          <span className="text-slate-700">{r.value}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </div>
        )}
      </Dialog>
    </details>
  );
}

export default KickoffDataPanel;
